import { Badge, Input, Textarea } from '../../components/ui';
import { OpportunityDetail, OpportunityResultRow, OpportunityScore, ScoringState } from './api';
import {
  displaySource,
  gainLabels,
  gainWeights,
  OpportunityStatus,
  painLabels,
  painWeights,
  STATUS_OPTIONS,
  STRATEGIC_LABELS,
} from './publicScoring';

const EDITABLE_DETAIL_LABELS = new Set([
  'Business Problem',
  'AI Solution',
  'Potential Impact',
  'Feasibility / Constraints',
  'Evidence Summary',
  'Evidence + Caveat',
  'Source Systems',
  'Data Fields Needed',
  'Pilot Wedge',
  'Owner',
  'Decision / Wave',
  'Confidence',
]);

interface OpportunityDetailPanelProps {
  row: OpportunityResultRow;
  opportunity?: OpportunityScore;
  detail?: OpportunityDetail;
  state: ScoringState;
  onScoreChange: (id: string, kind: 'gain' | 'pain', index: number, value: number) => void;
  onStatusChange: (id: string, status: OpportunityStatus) => void;
  onDetailFieldChange: (id: string, label: string, value: string) => void;
}

export default function OpportunityDetailPanel({
  row,
  opportunity,
  detail,
  state,
  onScoreChange,
  onStatusChange,
  onDetailFieldChange,
}: OpportunityDetailPanelProps) {
  const fields = detail?.fields ?? [];
  const submitter = opportunity?.submitter ?? detail?.submitter ?? row.submitter;
  const currentStatus = (opportunity?.status ?? row.status ?? 'active') as OpportunityStatus;

  return (
    <div className="space-y-5 bg-[#FFF8F5] px-5 py-5 dark:bg-dark-surface-2">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{row.id}</Badge>
            <Badge variant="secondary">{displaySource(row.source)}</Badge>
            {(row.strategic_tags ?? []).map((tag) => (
              <Badge key={tag} variant="secondary">{STRATEGIC_LABELS[tag] ?? tag}</Badge>
            ))}
          </div>
          <div className="max-w-xs">
            <span className="mb-1 block text-micro font-bold uppercase tracking-wide text-[#B8371E] dark:text-orange-200">
              Status
            </span>
            <select
              aria-label={`${row.name} status`}
              value={currentStatus}
              onChange={(event) => onStatusChange(row.id, event.target.value as OpportunityStatus)}
              className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-small text-charcoal shadow-sm focus:border-eliza-red focus:outline-none focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {submitter && (
            <p className="text-small text-gray-500 dark:text-gray-400">{`Submitter: ${submitter}`}</p>
          )}
          {detail?.note && (
            <p className="rounded-md border border-orange-200 bg-orange-50 p-3 text-small text-orange-900 dark:border-orange-900/50 dark:bg-orange-950/30 dark:text-orange-100">
              {detail.note}
            </p>
          )}
          {fields.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2">
              {fields.map(([label, value]) => {
                const isEditable = EDITABLE_DETAIL_LABELS.has(label);
                return (
                  <section key={label} className="rounded-md border border-gray-200 bg-white p-3 dark:border-dark-border dark:bg-dark-surface">
                    <label className="block">
                      <span className="text-micro font-bold uppercase tracking-wide text-[#B8371E] dark:text-orange-200">
                        {label}
                      </span>
                      {isEditable ? (
                        <Textarea
                          aria-label={`${label} detail`}
                          value={value}
                          onChange={(event) => onDetailFieldChange(row.id, label, event.target.value)}
                          className="mt-2 min-h-[104px] resize-y border-0 bg-transparent px-0 py-0 text-small leading-6 shadow-none focus:ring-0 dark:bg-transparent"
                        />
                      ) : (
                        <Markdownish text={value} />
                      )}
                    </label>
                  </section>
                );
              })}
            </div>
          ) : (
            <p className="text-small text-gray-600 dark:text-gray-300">{row.description ?? 'No detail notes available.'}</p>
          )}
        </div>
        <div className="rounded-md border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
          <h4 className="text-micro font-bold uppercase tracking-[0.18em] text-[#B8371E] dark:text-orange-200">
            Factor scores · click to edit
          </h4>
          <p className="mt-1 text-small text-gray-500 dark:text-gray-400">
            Scores save only when you choose Save My View.
          </p>
          <FactorTable
            title="Gain"
            kind="gain"
            colorClass="bg-eliza-red"
            labels={gainLabels(state)}
            weights={gainWeights(state)}
            scores={opportunity?.gain ?? row.gain ?? []}
            rowId={row.id}
            onScoreChange={onScoreChange}
          />
          <FactorTable
            title="Pain"
            kind="pain"
            colorClass="bg-slate-500"
            labels={painLabels(state)}
            weights={painWeights(state)}
            scores={opportunity?.pain ?? row.pain ?? []}
            rowId={row.id}
            onScoreChange={onScoreChange}
          />
        </div>
      </div>
    </div>
  );
}

function FactorTable({
  title,
  kind,
  labels,
  weights,
  scores,
  rowId,
  colorClass,
  onScoreChange,
}: {
  title: string;
  kind: 'gain' | 'pain';
  labels: string[];
  weights: number[];
  scores: number[];
  rowId: string;
  colorClass: string;
  onScoreChange: (id: string, kind: 'gain' | 'pain', index: number, value: number) => void;
}) {
  return (
    <div className="mt-4">
      <div className="mb-3 flex items-center justify-between text-small font-bold">
        <span>{title} — weighted index: {weightedTotal(scores, weights)} / 100</span>
        <span className="text-gray-500 dark:text-gray-400">Weight</span>
      </div>
      <div className="space-y-2">
        {labels.map((label, index) => {
          const score = scores[index] ?? 3;
          return (
            <div key={`${kind}-${label}`} className="grid grid-cols-[minmax(160px,1fr)_80px_52px] items-center gap-3 text-small">
              <span className="text-gray-700 dark:text-gray-200">{label}</span>
              <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-dark-surface-2">
                <div className={`h-full ${colorClass}`} style={{ width: `${score * 20}%` }} />
              </div>
              <Input
                type="number"
                min={1}
                max={5}
                aria-label={`${label} score`}
                value={score}
                onChange={(event) => onScoreChange(rowId, kind, index, Number(event.target.value))}
                className="h-8 px-2 text-center"
              />
              <span className="col-start-2 text-right text-micro text-gray-500 dark:text-gray-400">
                {Math.round((weights[index] ?? 0) * 100)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function weightedTotal(scores: number[], weights: number[]) {
  const weighted = scores.slice(0, weights.length).reduce((sum, score, index) => sum + score * (weights[index] ?? 0), 0);
  return Math.min(100, Math.max(0, Math.round(((weighted - 1) / 4) * 100)));
}

function Markdownish({ text }: { text: string }) {
  return (
    <p className="mt-1 whitespace-pre-line text-small leading-6 text-gray-600 dark:text-gray-300">
      {text.replace(/\*\*/g, '').replace(/\*/g, '')}
    </p>
  );
}
